import { z } from 'zod';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Yakıt ana kaydı (ADR 0117 §1, §8; ortak belgesi §6 "Yakıt ana kaydı"): kod, ad, kategori, grade,
 * emisyon faktör sınıfı ve aktiflik. Şirket verisidir: bulutta yazılır, `fuel_management`
 * yeteneğini bildiren gemiye salt okunur iner. Ticari ad (VLSFO vb.) emisyon faktörünü belirlemez;
 * faktörü doğrulanmış **faktör sınıfı** belirler. Sınıf boşsa yakıt "doğrulanmamış"tır ve MRV
 * hesabında incelemeye düşer (eksik faktör sıfır sayılmaz).
 */

/** Yakıt kategorileri (ISO 8217 residual/distillate; gaz ve alternatif yakıtlar). */
export const FUEL_CATEGORIES = [
  'residual',
  'distillate',
  'lng',
  'lpg',
  'methanol',
  'ethanol',
  'biofuel',
  'other',
] as const;
export type FuelCategory = (typeof FUEL_CATEGORIES)[number];

/**
 * Emisyon faktör sınıfları (ADR 0117 §8; Regulation (EU) 2015/757 Annex I, 2023/2776 ile
 * değişik): LNG motor teknolojisiyle (Otto orta/yavaş devir, Diesel), LPG propan/bütan ayrı.
 */
export const FACTOR_CLASSES = [
  'HFO',
  'LFO',
  'MDO_MGO',
  'LNG_OTTO_MS',
  'LNG_OTTO_SS',
  'LNG_DIESEL',
  'LPG_PROPANE',
  'LPG_BUTANE',
  'METHANOL',
  'ETHANOL',
  'OTHER',
] as const;
export type FactorClass = (typeof FACTOR_CLASSES)[number];

/** Yakıt kodu: büyük harf, rakam ve `.`, `_`, `-`; şirkette tekil. */
export const FUEL_CODE_PATTERN = /^[A-Z0-9][A-Z0-9._-]{0,19}$/;

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const requiredText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

export const fuelTypeSchema = z.strictObject({
  code: z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.trim())
    .pipe(z.string().min(1, 'REQUIRED').regex(FUEL_CODE_PATTERN, 'INVALID_CODE')),
  name: requiredText(80),
  category: z.enum(FUEL_CATEGORIES, typed('INVALID_OPTION')),
  grade: optionalText(40),
  /** Doğrulanmış faktör sınıfı; boş: doğrulanmamış (MRV'de inceleme). */
  factorClass: z
    .enum(FACTOR_CLASSES, typed('INVALID_OPTION'))
    .nullish()
    .transform((value) => value ?? null),
  active: z
    .boolean(typed('INVALID_BOOLEAN'))
    .nullish()
    .transform((value) => value ?? true),
  remarks: optionalText(500),
});
export type FuelTypeInput = z.output<typeof fuelTypeSchema>;

export function parseFuelType(input: unknown): ParseResult<FuelTypeInput> {
  const result = fuelTypeSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : {
        ok: false,
        issues: toIssues(result.error).filter(
          (issue, index, all) => all.findIndex((i) => i.path === issue.path) === index,
        ),
      };
}

/** Eski sefer raporu yakıt kodlarının (`FUEL_TYPES`) önerilen ana kaydı. */
const LEGACY: Readonly<
  Record<string, { name: string; category: FuelCategory; factorClassHint: FactorClass | null }>
> = {
  HFO: { name: 'Heavy Fuel Oil', category: 'residual', factorClassHint: 'HFO' },
  // VLSFO/LSFO/ULSFO farklı grade sınıflarını içerir: ticari ad faktörü belirlemez (ADR 0117 §8)
  VLSFO: { name: 'Very Low Sulphur Fuel Oil', category: 'residual', factorClassHint: null },
  LSFO: { name: 'Low Sulphur Fuel Oil', category: 'residual', factorClassHint: null },
  ULSFO: { name: 'Ultra Low Sulphur Fuel Oil', category: 'residual', factorClassHint: null },
  MDO: { name: 'Marine Diesel Oil', category: 'distillate', factorClassHint: 'MDO_MGO' },
  MGO: { name: 'Marine Gas Oil', category: 'distillate', factorClassHint: 'MDO_MGO' },
  LSMGO: { name: 'Low Sulphur Marine Gas Oil', category: 'distillate', factorClassHint: 'MDO_MGO' },
  // LNG'nin sınıfı motor teknolojisine bağlıdır (Otto/Diesel): kayıt seçer
  LNG: { name: 'Liquefied Natural Gas', category: 'lng', factorClassHint: null },
};

/**
 * Eski yakıt kodunun önerilen ana kaydı: yalnız öneri. Faktör sınıfı **boş** gelir (onaysız);
 * `factorClassHint` ekranda ipucu olarak gösterilir, kullanıcı seçmeden kayda girmez.
 */
export function suggestFuelType(code: string): {
  code: string;
  name: string;
  category: FuelCategory;
  grade: null;
  factorClass: null;
  factorClassHint: FactorClass | null;
  active: true;
} {
  const known = LEGACY[code];
  return {
    code,
    name: known?.name ?? code,
    category: known?.category ?? 'other',
    grade: null,
    factorClass: null,
    factorClassHint: known?.factorClassHint ?? null,
    active: true,
  };
}
